import React from 'react';
import { graphql, useFragment } from 'react-relay';
import Tooltip from '@atlaskit/tooltip';
import {
	type StatusCategory,
	TODO,
	IN_PROGRESS,
	DONE,
	categoryIdForStatusCategory,
} from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { issueProgressBar_versionProgress_IssuesProgressBar$key } from '@atlassian/jira-relay/src/__generated__/issueProgressBar_versionProgress_IssuesProgressBar.graphql';
import { useFilterSortingQueryParams } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/filter-sorting-query-param/index.tsx';
import { DONE_COLOR, IN_PROGRESS_COLOR, TODO_COLOR } from '../../common/constants.tsx';
import {
	Container,
	ProgressBarContainer,
	ProgressBarElementContainer,
	Bar,
} from '../../common/ui/styled.tsx';
import messages from './messages.tsx';

export type Props = {
	jiraVersion: issueProgressBar_versionProgress_IssuesProgressBar$key;
};

export const IssuesProgressBar = ({ jiraVersion }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showStatusCategoryIssues } = useFilterSortingQueryParams();

	const version = useFragment(
		graphql`
			fragment issueProgressBar_versionProgress_IssuesProgressBar on JiraVersion {
				doneIssuesRedesignTemp: issues(filters: { statusCategories: [DONE] })
					@required(action: THROW) {
					totalCount @required(action: THROW)
				}
				inProgressIssuesRedesignTemp: issues(filters: { statusCategories: [IN_PROGRESS] })
					@required(action: THROW) {
					totalCount @required(action: THROW)
				}
				todoIssuesRedesignTemp: issues(filters: { statusCategories: [TODO] })
					@required(action: THROW) {
					totalCount @required(action: THROW)
				}
			}
		`,
		jiraVersion,
	);

	const inProgressIssueCount = version.inProgressIssuesRedesignTemp.totalCount;
	const doneIssueCount = version.doneIssuesRedesignTemp.totalCount;
	const todoIssueCount = version.todoIssuesRedesignTemp.totalCount;
	const totalIssueCount = inProgressIssueCount + doneIssueCount + todoIssueCount;

	const createStatusClickHandler = (category: StatusCategory) => () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'progressBarShortcutFilter',
			{ filter: category },
		);
		const categoryId = String(categoryIdForStatusCategory(category));
		showStatusCategoryIssues([categoryId]);
	};

	if (totalIssueCount === 0) {
		return (
			<Container>
				<ProgressBarContainer>
					<ProgressBarElementContainer totalCount={1} portionCount={1} isClickable isLast>
						<Bar color={TODO_COLOR} isFirst isLast />
					</ProgressBarElementContainer>
				</ProgressBarContainer>
			</Container>
		);
	}

	return (
		<Container>
			<ProgressBarContainer>
				{doneIssueCount > 0 && (
					<ProgressBarElementContainer
						onClick={createStatusClickHandler(DONE)}
						totalCount={totalIssueCount}
						portionCount={doneIssueCount}
						isClickable
						isLast={totalIssueCount === doneIssueCount}
					>
						<Tooltip
							position="top"
							content={formatMessage(messages.doneIssuesTooltipText, {
								completedIssueCount: doneIssueCount,
								totalIssueCount,
							})}
						>
							<Bar color={DONE_COLOR} isFirst isLast={totalIssueCount === doneIssueCount} />
						</Tooltip>
					</ProgressBarElementContainer>
				)}
				{inProgressIssueCount > 0 && (
					<ProgressBarElementContainer
						totalCount={totalIssueCount}
						portionCount={inProgressIssueCount}
						onClick={createStatusClickHandler(IN_PROGRESS)}
						isClickable
						isLast={todoIssueCount === 0}
					>
						<Tooltip
							position="top"
							content={formatMessage(messages.inProgressIssuesTooltipText, {
								inProgressIssueCount,
								totalIssueCount,
							})}
						>
							<Bar
								color={IN_PROGRESS_COLOR}
								isFirst={doneIssueCount === 0}
								isLast={todoIssueCount === 0}
							/>
						</Tooltip>
					</ProgressBarElementContainer>
				)}
				{todoIssueCount > 0 && (
					<ProgressBarElementContainer
						isLast
						totalCount={totalIssueCount}
						portionCount={todoIssueCount}
						onClick={createStatusClickHandler(TODO)}
						isClickable
					>
						<Tooltip
							position="top"
							content={formatMessage(messages.todoIssuesTooltipText, {
								todoIssueCount,
								totalIssueCount,
							})}
						>
							<Bar color={TODO_COLOR} isFirst={totalIssueCount === todoIssueCount} isLast />
						</Tooltip>
					</ProgressBarElementContainer>
				)}
			</ProgressBarContainer>
		</Container>
	);
};
